import * as Yup from 'yup';
import { useFormik } from 'formik';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { Seo } from 'src/components/seo';
import { useMounted } from 'src/hooks/use-mounted';
import {
  resetSetPassword,
  resetTherapistLogin,
  autoGeneratePassword,
  postResetPasswordAction,
  PostResetPasswordResetState,
  therapistLogout,
} from 'src/redux/actions/authActions';
import { useSelector, useDispatch } from 'react-redux';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';
import Link from '@mui/material/Link';
import { Box, IconButton, InputAdornment, SvgIcon, Typography } from '@mui/material';
import ArrowLeftIcon from '@untitled-ui/icons-react/build/esm/ArrowLeft';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { getRealmNameForLocalLogout } from 'src/utils/generate-url';
import AuthContext from 'src/routes/AuthContext';

const getInitialValues = () => ({
  oldPassword: '',
  password: '',
  passwordConfirm: '',
  submit: null,
});

const passwordRules = [
  /(?=.*[A-Z])/,
  /(?=.*[a-z])/,
  /(?=.*\d)/,
  /(?=.*[!@#$%^&*])/,
  /^[^\s]{8,16}$/,
];

const passwordValidationSchema = Yup.string()
  .required('Please enter a valid New Password')
  .test('password-validation', 'Password should contain at least one uppercase letter.', (value) =>
    passwordRules[0].test(value)
  )
  .test('password-validation', 'Password should contain at least one lowercase letter', (value) =>
    passwordRules[1].test(value)
  )
  .test('password-validation', 'Password should contain at least one numeric digit', (value) =>
    passwordRules[2].test(value)
  )
  .test(
    'password-validation',
    'Password should contain at least one special character (!@#$%^&*)',
    (value) => passwordRules[3].test(value)
  )
  .test(
    'password-validation',
    'Password must be at least 8 characters long and cannot exceed 16 characters',
    (value) => passwordRules[4].test(value)
  );

const validationSchema = Yup.object({
  oldPassword: Yup.string().required('Please enter a valid Current Password'),
  password: passwordValidationSchema,
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref('password')], 'New Password and Confirm Password should match')
    .required('Please enter a valid Confirm Password'),
});

const Page = () => {
  const isMounted = useMounted();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const autoGeneratedPass = useSelector((state: any) => state.autoGeneratePasswordReducer);
  const postPasswordReset = useSelector((state: any) => state.postPasswordResetReducer);

  const [error, setError] = useState('');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(),
    validationSchema: validationSchema,
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        dispatch(
          postResetPasswordAction({
            oldPassword: values.oldPassword,
            newPassword: values.password,
          })
        );
      } catch (err) {
        console.error(err);

        if (isMounted()) {
          helpers.setStatus({ success: false });
          helpers.setErrors({ submit: err.message });
          helpers.setSubmitting(false);
        }
      }
    },
  });

  const auth = useContext(AuthContext);
  const checkHost = window.location.hostname;

  useEffect(() => {
    if (postPasswordReset?.isSuccess) {
      setError('');
      dispatch(
        therapistLogout(
          { refresh_token: auth.refreshToken, client_id: 'js-client', client_secret: '' },
          checkHost === 'localhost' || checkHost === '127.0.0.1'
            ? getRealmNameForLocalLogout() || ''
            : checkHost.split('-')[0]
        )
      );
      dispatch(PostResetPasswordResetState());
      dispatch(resetTherapistLogin());
      dispatch(resetSetPassword());
      navigate(paths.landingPageLogout);
      return;
    } else {
      if (postPasswordReset?.isSuccess !== null) {
        setError(postPasswordReset?.response?.message);
        return;
      }
    }
  }, [postPasswordReset]);

  useEffect(() => {
    if (!!autoGeneratedPass?.password) {
      formik.setFieldValue('password', autoGeneratedPass?.password);
      formik.setFieldValue('passwordConfirm', autoGeneratedPass?.password);
    }
  }, [autoGeneratedPass]);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const getAutoGenerate = () => {
    dispatch(autoGeneratePassword());
  };

  const handleBackToResetPass = () => {
    dispatch(PostResetPasswordResetState());
    navigate(-1);
  };

  return (
    <>
      <Seo title="Reset Password" />
      <div>
        <Card
          sx={{ pl: 2 }}
          elevation={16}
        >
          <CardHeader
            sx={{ pb: 0 }}
            title={
              <Box sx={{ display: 'flex', gap: 0.1, alignItems: 'center', margin: '0px -21px' }}>
                <Link
                  color="text.primary"
                  component="button"
                  href={paths.index}
                  onClick={handleBackToResetPass}
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                  }}
                  underline="hover"
                >
                  <SvgIcon sx={{ mr: 1 }}>
                    <ArrowLeftIcon />
                  </SvgIcon>
                  <Typography variant="subtitle2">Back To Dashboard</Typography>
                </Link>
              </Box>
            }
          ></CardHeader>

          <CardContent>
            <Box
              pt={3}
              pb={4}
            >
              <Typography variant="h6">Restore Insights - Reset Password</Typography>
            </Box>
            <form
              noValidate
              onSubmit={formik.handleSubmit}
            >
              <Stack spacing={3}>
                <Box mb={4}>
                  <Typography
                    mb={1.5}
                    variant="subtitle2"
                  >
                    To continue, First Verify your current password.
                  </Typography>
                  <TextField
                    error={!!(formik.touched.oldPassword && formik.errors.oldPassword)}
                    fullWidth
                    helperText={formik.touched.oldPassword && formik.errors.oldPassword}
                    label="Current Password"
                    type={showPassword.current ? 'text' : 'password'}
                    name="oldPassword"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.trim();
                      formik.setFieldValue('oldPassword', trimmedValue);
                    }}
                    value={formik.values.oldPassword}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setShowPassword((prev) => ({
                                ...prev,
                                current: !prev.current,
                              }))
                            }
                          >
                            {showPassword.current ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="subtitle2">
                    To reset, Enter new strong password or auto-generate.
                  </Typography>
                  <TextField
                    error={!!(formik.touched.password && formik.errors.password)}
                    fullWidth
                    helperText={formik.touched.password && formik.errors.password}
                    label="New Password"
                    name="password"
                    type={showPassword.new ? 'text' : 'password'}
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.trim();
                      formik.setFieldValue('password', trimmedValue);
                    }}
                    value={formik.values.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setShowPassword((prev) => ({
                                ...prev,
                                new: !prev.new,
                              }))
                            }
                          >
                            {showPassword.new ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    error={!!(formik.touched.passwordConfirm && formik.errors.passwordConfirm)}
                    fullWidth
                    helperText={formik.touched.passwordConfirm && formik.errors.passwordConfirm}
                    label="Confirm Password"
                    name="passwordConfirm"
                    onBlur={formik.handleBlur}
                    type={showPassword.confirm ? 'text' : 'password'}
                    onChange={(e) => {
                      const trimmedValue = e.target.value.trim();
                      formik.setFieldValue('passwordConfirm', trimmedValue);
                    }}
                    value={formik.values.passwordConfirm}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() =>
                              setShowPassword((prev) => ({
                                ...prev,
                                confirm: !prev.confirm,
                              }))
                            }
                          >
                            {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box>
                    <Button
                      focusRipple
                      variant="text"
                      size="small"
                      disabled={autoGeneratedPass?.loading}
                      onClick={getAutoGenerate}
                    >
                      Auto-generate Password
                    </Button>
                  </Box>
                </Box>
              </Stack>
              {error && (
                <FormHelperText
                  error
                  sx={{ mt: 3 }}
                >
                  {error as string}
                </FormHelperText>
              )}
              <LoadingButton
                disabled={formik.isSubmitting || postPasswordReset?.loading}
                fullWidth
                size="large"
                sx={{ mt: 3 }}
                type="submit"
                variant="contained"
                loadingPosition="end"
                loading={postPasswordReset?.loading}
              >
                Reset Password
              </LoadingButton>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default Page;
